import { Component, Inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';

import { BaseComponent } from '../../core';
import { PinCodeInputModule } from '../pin-code-input';
import {
  AbstractStateService,
  DialogService,
  ErrorHandlerServiceService,
  ResendPinCountdownService,
  UserService,
} from '../../services';
import {
  LibCoreLayoutModule,
  SuccessCheckAnimationDialogComponent,
  SuccessCheckAnimationDialogComponentData,
} from '../core-layout';

export interface EmailPhoneConfirmationPinDialogComponentData {
  challengeId: string;
  username: string;
  isConfirmEmail: boolean;
}

@Component({
  selector: 'lib-email-phone-confirmation-pin-dialog',
  templateUrl: './email-phone-confirmation-pin-dialog.component.html',
  styleUrls: ['./email-phone-confirmation-pin-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    TranslateModule,
    PinCodeInputModule,
    ReactiveFormsModule,
    // material design
    MatButtonModule,
  ],
})
export class EmailPhoneConfirmationPinDialogComponent extends BaseComponent {
  public isLoading = false;
  public resendPinCountdownInSecond = 0;
  public isConfirmEmail: boolean;

  public username: string | undefined;

  private challengeId: string | undefined;

  public controlPin = new FormControl(null, [Validators.required]);

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data: EmailPhoneConfirmationPinDialogComponentData,
    private dialogRef: MatDialogRef<EmailPhoneConfirmationPinDialogComponent>,
    private dialogService: DialogService,
    private errorHandlerServiceService: ErrorHandlerServiceService,
    private stateService: AbstractStateService,
    private resendPinCountdownService: ResendPinCountdownService,
    private userService: UserService
  ) {
    super();

    this.challengeId = this.data?.challengeId;
    this.username = this.data?.username;
    this.isConfirmEmail = !!this.data?.isConfirmEmail;

    this.resendPinCountdownService.resendPinCountdownInSecond$
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((v) => (this.resendPinCountdownInSecond = v));
  }

  public isAbleToSubmit(): boolean {
    return !this.isLoading && this.controlPin.valid;
  }

  public async submit() {
    if (!this.isAbleToSubmit()) {
      return;
    }

    try {
      this.isLoading = true;

      const token = await this.userService.confirmPhoneNrOrEmail(
        this.challengeId,
        this.controlPin.value
      );
      if (token?.length) {
        this.stateService.setToken(`Bearer ${token}`);
        this.displaySuccessCheckAnimationDialog();
        this.closeDialog(true);
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public isAbleToResendPin(): boolean {
    return (
      !this.isLoading && this.resendPinCountdownInSecond <= 0 && !!this.username
    );
  }

  public async resendPin() {
    if (!this.isAbleToResendPin()) {
      return;
    }

    try {
      this.isLoading = true;

      if (this.isConfirmEmail) {
        const response = await this.userService.getCodeToConfirmEmail(
          this.username
        );
        this.challengeId = response?.id;
      } else {
        const response = await this.userService.getCodeToConfirmPhoneNumber(
          this.username
        );
        this.challengeId = response?.id;
      }

      if (this.challengeId?.length) {
        this.resendPinCountdownService.setResendPinCountdownNow();
      }
    } catch (e) {
      this.errorHandlerServiceService.handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  public closeDialog(isConfirmed?: boolean): void {
    this.dialogRef.close(isConfirmed);
  }

  private displaySuccessCheckAnimationDialog(): void {
    this.dialogService.openDialog(
      SuccessCheckAnimationDialogComponent,
      this.dialogService.createSmallDialogConfig({
        text: 'email-phone-confirmation-pin-dialog.text.update-success',
      } as SuccessCheckAnimationDialogComponentData)
    );
  }
}
